import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';

import { datadogRum } from '@datadog/browser-rum';
import { IonContent, IonMenu, NavController } from '@ionic/angular';
import { Capacitor, PluginListenerHandle } from '@capacitor/core';
import {
  ActionPerformed,
  PushNotifications,
  PushNotificationSchema
} from '@capacitor/push-notifications';
import {
  catchError,
  combineLatest,
  concatMap,
  exhaustMap,
  filter,
  from,
  interval,
  merge,
  Observable,
  of,
  Subscription,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs';

import {
  FcmTokenTypes,
  UserLocationRole,
  UserRole,
} from '@shared/user/enums';
import { AuthUser } from '@shared/signup/models';
import { ConfirmationModalRef, ConfirmationModalService, ConnectionAlertService } from '@tablesready/common-ui';
import { NavigationService } from '@tablesready/frontend-navigation';
import { WebSocketEventsEnum } from '@shared/websocket';
import { LocationSwitcherService } from '@tablesready/frontend-location-switcher';
import {
  desktopMenuBreakpoint,
  desktopSettingsMenuBreakpoint,
  FrontendBreakpointsService
} from '@tablesready/frontend-breakpoints';
import { AppIcon, AppIconSize } from '@tablesready/frontend-icons';
import { AuthRestaurantService, SocketService } from '@tablesready/frontend-store';
import { StatusService } from '@tablesready/frontend-status-bar';
import { AuthService, NewAccountRumTracker, TabIdentityService } from '@tablesready/frontend-auth';
import { LocalStorageService } from '@tablesready/frontend-storage';
import { APP_VARIABLES_TOKEN, AppVariables } from '@tablesready/frontend-variables';
import { NAVIGATION_ITEMS, NavigationItem } from '@tablesready/frontend-nav-sidebar';
import {
  BillingVisibility,
  FrontendPaidFeatureService
} from '@tablesready/frontend-paid-feature-alert';
import { FrontendNetworkService } from '@tablesready/frontend-network';
import {
  AuthenticatedInitializer,
  FcmTokenApiService,
  PermissionCheckerService,
  PushNotificationsService,
  ReservationService
} from '../../services';
import { CapacitorEventBird } from 'capacitor-event-bird';
import { FrontendResumeBackgroundService, FrontendResumeReason } from '@tablesready/frontend-resume-background';
import { Party } from '@shared/party';
import { ProfileInvitesService } from '../../profile/services';
import { ConsentService } from '../../consent/consent.service';

const NEW_ACCOUNT_RUM_CHECK_MS = 10_000;

@Component({
  selector: 'tablesready-authenticated-layout',
  styleUrls: ['./authenticated-layout.component.scss'],
  templateUrl: './authenticated-layout.component.html',
  providers: [AuthenticatedInitializer, PermissionCheckerService],
  standalone: false,
})
export class AuthenticatedLayoutComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(IonContent) content?: IonContent;
  @ViewChild('sidebarRef') sidebarRef?: ElementRef;
  @ViewChild('belowHeaderSection') belowHeaderSection?: ElementRef;
  navItems: NavigationItem[] = [];
  observer: ResizeObserver;
  path: string;

  showDesktopMenu = false;
  isSettingsPage = false;
  settingsPaneOpen = false;
  defaultPage = '/';
  iconSize = AppIconSize;
  icon = AppIcon;
  user?: AuthUser;

  private readonly resumeService = inject(FrontendResumeBackgroundService);
  private readonly consentService = inject(ConsentService);
  private readonly tabIdentity = inject(TabIdentityService);
  private readonly newAccountRum = new NewAccountRumTracker(inject(LocalStorageService));
  private notificationListener?: PluginListenerHandle;
  private notificationTapListener?: PluginListenerHandle;
  private fcmTokenRefreshListener?: PluginListenerHandle;

  private readonly socketHealthCheckIntervalMs = 15000;
  private readonly subscriptions = new Subscription();

  constructor(
    private readonly authService: AuthService,
    private readonly statusService: StatusService,
    private readonly socketService: SocketService,
    private readonly connectionAlertService: ConnectionAlertService,
    private readonly navService: NavigationService,
    private readonly navController: NavController,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly confirmationService: ConfirmationModalService,
    private readonly cdr: ChangeDetectorRef,
    private readonly paidService: FrontendPaidFeatureService,
    private readonly authRestaurantService: AuthRestaurantService,
    private readonly authenticatedInitializer: AuthenticatedInitializer,
    private readonly reservationService: ReservationService,
    private readonly inviteDataService: ProfileInvitesService,
    private readonly fcmService: FcmTokenApiService,
    private readonly pushService: PushNotificationsService,
    private readonly locationSwitcherService: LocationSwitcherService,
    private readonly breakpointObserver: BreakpointObserver,
    private readonly breakpointService: FrontendBreakpointsService,
    private readonly networkService: FrontendNetworkService,
    @Inject(APP_VARIABLES_TOKEN) private readonly appVariables: AppVariables,
  ) {
    this.path = this.appVariables.WHITELABEL_BRAND.toLowerCase();
  }

  ngAfterViewInit(): void {
    this.observer = new ResizeObserver((_) => {
      this.breakpointService.setMainNavSidebarWidth(this.sidebarRef.nativeElement.offsetWidth);
    });

    this.observer.observe(this.sidebarRef.nativeElement);
  }

  goRoot(): void {
    void this.navController.navigateRoot(this.defaultPage);
  }

  ngOnInit(): void {
    // App users are auto opted-in to analytics per the Terms of Service. This
    // writes/refreshes the shared tr_consent cookie (analytics granted, ads/embeds
    // preserved) and dismisses the consent banner for authenticated sessions.
    this.consentService.grantAnalyticsConsent();
    // Signed-in use only (see the RUM init in AppComponent); the replay sample rate
    // still applies, except to new accounts (NewAccountRumTracker)
    datadogRum.startSessionReplayRecording();

    const activatedRouteSub = this.route.queryParams
      .pipe(
        filter(params => Object.values(params).length > 0),
        take(1),
        tap(params => {
          const locationToSwitch = params['location'];

          if (this.authService.isAuthenticated() && locationToSwitch) {
            const currentLocation = this.locationSwitcherService.selectedLocation$.getValue().location._id;

            if (currentLocation !== locationToSwitch) {
              this.locationSwitcherService.selectLocation(locationToSwitch, true);
            }
          }
        })
      ).subscribe();
    this.subscriptions.add(activatedRouteSub);

    this.subscriptions.add(combineLatest([
      this.authRestaurantService.select(),
      this.inviteDataService.get(),
      this.breakpointObserver.observe([Breakpoints.Handset, Breakpoints.HandsetLandscape]),
    ])
      .pipe(tap(([user, accountsAccess, { matches: isMobileScreen }]) => {
        this.user = user;
        this.paidService.setUser(user);
        const navigationItems = [...NAVIGATION_ITEMS].filter((navItem) => {

          if (
            this.paidService.billingsVisibility === BillingVisibility.HIDDEN_ON_MOBILE &&
            (navItem.visibility === BillingVisibility.LINKS_ONLY_ON_MOBILE ||
              navItem.visibility === BillingVisibility.STARTER_PLAN_OUTSIDE_US ||
              navItem.visibility === BillingVisibility.BUSINESS_PLAN_OUTSIDE_US)
          ) {
            return false;
          }

          if (
            navItem.visibility === BillingVisibility.BUSINESS_PLAN_OUTSIDE_US &&
            this.paidService.billingsVisibility === BillingVisibility.STARTER_PLAN_OUTSIDE_US
          ) {
            return false;
          }

          if (navItem.href === '/togo') {
            return !!(user.location?.displayOptions.togo && user.account?.features?.togo);
          }

          return true;
        });

        const hiddenLinks: Array<NavigationItem['href']> = [];
        this.statusService.setAccess(accountsAccess);

        if (user.role !== UserRole.admin) {
          hiddenLinks.push('/admin');
        }

        if (user.location) {
          if (user.locationUser.role === UserLocationRole.user) {
            hiddenLinks.push('/analytics', '/settings/preferences/notifications');
          }
        } else {
          hiddenLinks.push(
            '/waitlist',
            '/reservations',
            '/floorplan',
            '/history',
            '/analytics',
            '/customers',
            '/settings/preferences/notifications',
          );
        }

        this.navItems = navigationItems
          .filter(nav => !hiddenLinks.includes(nav.href))
          .map((item) => {
            const labels = user.location?.displayOptions?.labels;
            if (item.href === '/reservations') {
              return this.setMenuItemTittle(item, labels?.reservation_plural);
            }
            if (item.href === '/customers') {
              return this.setMenuItemTittle(item, labels?.customer_plural || 'Customers');
            }

            if (item.href === '/floorplan' && (isMobileScreen || !user.account?.features?.floorPlan)) {
              return this.setMenuItemTittle(item, 'Timeline', AppIcon.grid);
            }

            return item;
          });

        this.updateStatusBar(user);
        this.cdr.detectChanges();

        if (
          this.navService.currentUrl.includes('/togo') &&
          !(user.location?.displayOptions?.togo && user.account?.features?.togo)
        ) {
          void this.router.navigateByUrl('/waitlist');
        }
      }))
      .subscribe());

    this.subscriptions.add(this.authService.refreshTokens().subscribe());

    // `onAuthenticated` fires on initial connect and on every reconnect (socket.io v4), so it is
    // the single recovery trigger: clear the alert, refetch data, mark the connection restored.
    const connectionRestoredSub = this.socketService.onAuthenticated.subscribe(() => {
      this.connectionAlertService.dismiss();
      this.authenticatedInitializer.refetchData();
      this.networkService.socketConnectionRestored();
    });
    this.subscriptions.add(connectionRestoredSub);

    const sessionRevokedSub = this.socketService.onSessionRevoked
      .pipe(tap(() => this.authService.logoutWithExpiredSession()))
      .subscribe();
    this.subscriptions.add(sessionRevokedSub);

    // After a freeze the socket can be dead and the active token expired. Two triggers feed ONE
    // recovery stream: resume events (tab visible / back online), and a poll for always-on
    // terminals that may never fire a resume event yet can lose the socket to a server-initiated
    // disconnect (e.g. token expiry), which socket.io never retries. The single `exhaustMap`
    // serializes all recovery attempts (an in-flight refresh PUT is never aborted or duplicated),
    // and `refreshThenReconnect()` swallows errors so one failure can't kill the stream. The
    // offline filter skips ticks whose refresh PUTs are doomed anyway — the resume `online` event
    // re-enters the moment connectivity returns.
    const socketRecoverySub = merge(
      this.resumeService.resume$.pipe(filter(reason => reason !== FrontendResumeReason.tabClosed)),
      interval(this.socketHealthCheckIntervalMs).pipe(filter(() => !this.socketService.isConnected())),
    )
      .pipe(
        filter(() => this.resumeService.isOnline()),
        exhaustMap(() => this.refreshThenReconnect()),
      )
      .subscribe({ error: err => console.error('socket recovery stream error', err) });
    this.subscriptions.add(socketRecoverySub);

    const connectionErrorSub = this.socketService.onConnectionError.subscribe(() => {
      this.connectionAlertService.show();
    });
    this.subscriptions.add(connectionErrorSub);

    const locationChangeSub = this.authRestaurantService.selectAndDetectLocationChanges()
      .pipe(tap((data) => {
        this.updateStatusBar(data);
        this.authService.updateDataLayer(data);
        this.syncNewAccountRum(data);
      }))
      .subscribe();
    this.subscriptions.add(locationChangeSub);

    const newAccountRumSub = merge(
      this.router.events.pipe(filter(routerEvent => routerEvent instanceof NavigationEnd)),
      interval(NEW_ACCOUNT_RUM_CHECK_MS),
    )
      .pipe(withLatestFrom(this.authRestaurantService.select()))
      .subscribe(([, user]) => {
        this.syncNewAccountRum(user);
      });
    this.subscriptions.add(newAccountRumSub);

    const invitesSub = this.socketService.getEventStream(WebSocketEventsEnum.invite)
      .pipe(switchMap(() => this.inviteDataService.get()))
      .subscribe();
    this.subscriptions.add(invitesSub);

    const desktopSub = merge(
      this.breakpointService.observeDesktopMode(),
      this.breakpointService.observeSettingsDesktopMode(),
      this.navService.navEvents$,
    )
      .subscribe(() => {
        this.isSettingsPage = this.navService.currentUrl.includes('/settings');
        this.showDesktopMenu = this.isSettingsPage
          ? this.breakpointService.isSettingsDesktopMode : this.breakpointService.isDesktopMode;
      });
    this.subscriptions.add(desktopSub);

    if (!Capacitor.isNativePlatform()) {
      this.pushService.requestPermission();
      return;
    }

    combineLatest([
      from(CapacitorEventBird.getDeviceId()),
      from(CapacitorEventBird.getFCMToken({ value: '' })),
    ]).pipe(
      concatMap(([deviceId, token]) => {
        return this.fcmService.setFcmToken({
          deviceId: deviceId.value,
          token: token.value,
          type: FcmTokenTypes.ios,
        });
      }),
    ).subscribe();

    from(CapacitorEventBird.addListener('fcmTokenRefreshed', ({ token: newToken }) => {
      from(CapacitorEventBird.getDeviceId())
        .pipe(concatMap(({ value: deviceId }) => {
          return this.fcmService.setFcmToken({
            deviceId,
            token: newToken,
            type: FcmTokenTypes.ios,
          });
        }))
        .subscribe();
    })).subscribe((handle) => {
      this.fcmTokenRefreshListener = handle;
    });

    this.setupNativeNotifications();
  }

  setupNativeNotifications(): void {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    from(PushNotifications.addListener('pushNotificationReceived', (notification: PushNotificationSchema) => {}))
      .subscribe((pluginHandle) => {
        this.notificationListener = pluginHandle;
      });

    from(PushNotifications.addListener('pushNotificationActionPerformed', (notification: ActionPerformed) => {
      const locationId = notification.notification.data?.locationId;
      if (locationId) {
        this.locationSwitcherService.selectLocation(locationId, true);
        const destination = notification.notification.data?.url ?? '/waitlist';
        void this.router.navigateByUrl(destination);

        setTimeout(() => {
          this.resumeService.forceOnlineStatus();
        }, 1000);
      }
    }))
      .subscribe((pluginHandle) => {
        this.notificationTapListener = pluginHandle;
      });
  }

  sidebarItemClick(nav: NavigationItem, menu: IonMenu): void {
    this.settingsPaneOpen = nav.name === 'Settings';
    if (this.settingsPaneOpen) {
      void this.router.navigate([nav.href]);
      return;
    }

    void menu.close(true);
  }

  onMessageClicked(party: Party) {
    this.reservationService.chatParty(party, this.user);
  }

  ngOnDestroy(): void {
    // Also covers leaving without a logout (e.g. the guard redirecting after another tab
    // logged out)
    datadogRum.stopSessionReplayRecording();
    this.subscriptions.unsubscribe();

    if (this.observer) {
      this.observer.unobserve(this.sidebarRef.nativeElement);
    }

    if (this.notificationListener) {
      void this.notificationListener.remove();
    }

    if (this.notificationTapListener) {
      void this.notificationTapListener.remove();
    }

    if (this.fcmTokenRefreshListener) {
      void this.fcmTokenRefreshListener.remove();
    }
  }

  onLogout(): void {
    this.confirmationService.confirm({
      content: 'Are you sure you want to logout?',
      confirmBtnText: 'Logout',
      closeBtnText: 'Cancel',
    })
      .pipe(
        take(1),
        filter((data: ConfirmationModalRef<boolean>) => data.isConfirmed),
        tap(() => this.authService.logout()),
      )
      .subscribe()
  }

  /**
   * Refresh the active location's token (covering an access token that expired during a freeze, even
   * one shorter than the refresh gap), then re-establish the socket if it is down. Shared by the
   * resume and health-check triggers. Swallows errors so a single failure can never terminate the
   * recovery stream — the next resume/tick simply retries.
   */
  private refreshThenReconnect(): Observable<unknown> {
    return this.authService.ensureActiveTokenFresh().pipe(
      tap(() => {
        if (this.socketService.isConnected()) {
          // Socket healthy on resume: a lingering connection banner (e.g. raised by the HTTP error
          // interceptor for a transient blip) is stale — dismiss it here, since the `authenticated`
          // ack that normally owns dismissal only fires on a (re)connection.
          this.connectionAlertService.dismiss();
          return;
        }

        this.socketService.reconnectIfNeeded();
      }),
      catchError(() => of(null)),
    );
  }

  private setMenuItemTittle(
    item: NavigationItem,
    name: string,
    icon?: AppIcon,
  ) {
    item = { ...item };
    (item as any).name = name?.replace(/\b\w/, m => m.toUpperCase());

    if (icon) {
      item.icon = icon;
    }

    return item;
  }

  private updateStatusBar(user: AuthUser): void {
    this.statusService.updateStatusBarAlerts(user);
  }

  private syncNewAccountRum(user: AuthUser | undefined): void {
    // Another tab may have changed the login before its storage event reached this one
    this.tabIdentity.checkSharedLogin();
    this.newAccountRum.sync(user, this.authService.isAuthenticated() && !this.tabIdentity.isLocked());
  }

  protected readonly desktopSettingsMenuBreakpoint = desktopSettingsMenuBreakpoint;
  protected readonly desktopMenuBreakpoint = desktopMenuBreakpoint;
  protected readonly location = location;
}
